import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { adminService } from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPanel: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Fetch admin statistics
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery(
    'adminStats',
    adminService.getStats,
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Fetch WebSocket statistics
  const { data: wsStats, isLoading: wsLoading } = useQuery(
    'wsStats',
    adminService.getWebSocketStats,
    {
      refetchInterval: 5000, // Refetch every 5 seconds
    }
  );

  // Fetch logs
  const { data: logs, isLoading: logsLoading } = useQuery(
    'adminLogs',
    adminService.getLogs,
    {
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        🔧 Admin Panel
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Administrative interface for monitoring system status, user activity, and logs.
        </Typography>
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="System Stats" />
          <Tab label="WebSocket Status" />
          <Tab label="Application Logs" />
          <Tab label="Processing Records" />
        </Tabs>
      </Box>

      {/* System Statistics Tab */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              📊 System Statistics
            </Typography>
            
            {statsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : statsError ? (
              <Alert severity="error">
                Error loading statistics: {(statsError as any)?.message || 'Unknown error'}
              </Alert>
            ) : stats?.stats ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Total Records: ${stats.stats.total_records || 0}`}
                    color="primary"
                    size="medium"
                  />
                  <Chip 
                    label={`Completed: ${stats.stats.completed_records || 0}`}
                    color="success"
                    size="medium"
                  />
                  <Chip 
                    label={`Failed: ${stats.stats.failed_records || 0}`}
                    color="error"
                    size="medium"
                  />
                  <Chip 
                    label={`Success Rate: ${stats.stats.success_rate || 0}%`}
                    color="info"
                    size="medium"
                  />
                </Box>

                <Alert severity="success" sx={{ mb: 2 }}>
                  Database is available and connected
                </Alert>
              </Box>
            ) : (
              <Alert severity="warning">
                Database statistics not available
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* WebSocket Status Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              🌐 WebSocket Status
            </Typography>
            
            {wsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : wsStats ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Active Connections: ${wsStats.total_connections || 0}`}
                    color="primary"
                    size="medium"
                  />
                  <Chip 
                    label={`Active Users: ${wsStats.active_users?.length || 0}`}
                    color="secondary"
                    size="medium"
                  />
                </Box>

                {wsStats.active_users && wsStats.active_users.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Active Users:
                    </Typography>
                    {wsStats.active_users.map((user: string, index: number) => (
                      <Chip 
                        key={index}
                        label={user}
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="warning">
                WebSocket statistics not available
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Application Logs Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              📝 Application Logs
            </Typography>
            
            {logsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : logs?.logs ? (
              <Box>
                {Object.entries(logs.logs).map(([logFile, logLines]) => (
                  <Box key={logFile} sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      {logFile}
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        backgroundColor: '#f5f5f5', 
                        maxHeight: 400, 
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }}
                    >
                      {Array.isArray(logLines) ? (
                        logLines.map((line: string, index: number) => (
                          <div key={index}>{line}</div>
                        ))
                      ) : (
                        <div>{String(logLines)}</div>
                      )}
                    </Paper>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="warning">
                Logs not available
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Processing Records Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              📋 Recent Processing Records
            </Typography>
            
            {statsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : stats?.recent_records && stats.recent_records.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Filename</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Processing Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recent_records.map((record: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <div>{record.user_name || 'Unknown'}</div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {record.user_email || 'Unknown'}
                            </div>
                          </Box>
                        </TableCell>
                        <TableCell>{record.filename || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={record.status || 'Unknown'}
                            color={
                              record.status === 'completed' ? 'success' :
                              record.status === 'failed' ? 'error' :
                              record.status === 'processing' ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {record.submission_timestamp ? 
                            new Date(record.submission_timestamp).toLocaleString() : 
                            'Unknown'
                          }
                        </TableCell>
                        <TableCell>
                          {record.processing_duration_seconds ? 
                            `${record.processing_duration_seconds.toFixed(1)}s` : 
                            'N/A'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No processing records found
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

export default AdminPanel;


